import {
  eachDayOfInterval,
  endOfWeek,
  format,
  formatDuration,
  Locale,
  startOfWeek,
} from 'date-fns';

import { BatchWithClassRoutine } from '@/drizzle/models';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { bn, enUS } from 'date-fns/locale';
import { TimeZoneUtils } from './timezone-utils';

type TimeSlot = {
  value: string;
  label: string;
};

const engToBnNumbers: Record<string, string> = {
  '0': '০',
  '1': '১',
  '2': '২',
  '3': '৩',
  '4': '৪',
  '5': '৫',
  '6': '৬',
  '7': '৭',
  '8': '৮',
  '9': '৯',
};

function convertNumbersToBengali(hourAndMinute: string): string {
  return hourAndMinute.replace(
    /\d/g,
    (digit: string) => engToBnNumbers[digit] || digit,
  );
}

export const getWeekDays = (locale: Locale): string[] => {
  const now = new Date();
  const weekDays: string[] = [];
  const start = startOfWeek(now, { locale });
  const end = endOfWeek(now, { locale });

  eachDayOfInterval({ start, end }).forEach(day => {
    weekDays.push(format(day, 'EEEEEE', { locale }));
  });

  return weekDays;
};

export const getBengaliFormattedTime = (startTime: string): string => {
  const [hour, minuteAndPeriod] = startTime.split(':');
  const minuteParts = minuteAndPeriod.split(' ');
  const minute = minuteParts[0];
  const period = minuteParts[1];
  let timePeriod = '';

  const hourNumber = Number(hour);

  if (period === 'AM') {
    timePeriod = hourNumber < 6 || hourNumber === 12 ? 'রাত' : 'সকাল';
  } else {
    if (hourNumber === 12 || hourNumber < 4) {
      timePeriod = 'দুপুর';
    } else if (hourNumber < 6) {
      timePeriod = 'বিকাল';
    } else if (hourNumber < 7) {
      timePeriod = 'সন্ধ্যা';
    } else {
      timePeriod = 'রাত';
    }
  }

  const timeWithoutPeriod = `${hour}:${minute}`;
  const convertedHourAndMinute = convertNumbersToBengali(timeWithoutPeriod);

  const bengaliTime =
    minute === '00'
      ? `${convertedHourAndMinute} টা`
      : `${convertedHourAndMinute} মিনিট`;

  return `${timePeriod} ${bengaliTime}`;
};

export const dateUtils = {
  generateTimeSlots(
    startTime: string,
    endTime: string,
    interval: number = 15,
  ): TimeSlot[] {
    const timeSlots: TimeSlot[] = [];
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    let totalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = endHour * 60 + endMinute;

    while (totalMinutes <= endTotalMinutes) {
      const hours24 = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
      const amPm = hours24 < 12 || hours24 === 24 ? 'am' : 'pm';
      const value = `${String(hours24).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      const label = `${hours12}:${String(minutes).padStart(2, '0')}${amPm}`;

      timeSlots.push({ value, label });

      totalMinutes += interval;
    }

    return timeSlots;
  },
  formatter: {
    forUiOnlyDate: (dateString: string | number | Date) =>
      TimeZoneUtils.detectUserTimezoneAndDisplayDate(
        new Date(dateString),
        'MMM dd, yyyy',
      ),
    forUiOnlyTime: (dateString: string | number | Date) =>
      TimeZoneUtils.detectUserTimezoneAndDisplayDate(
        new Date(dateString),
        'hh:mm a',
      ),
    liveCardFormat: 'EEEE, d MMMM, h:mm a',
    forUiDateTime: (dateString: string | number | Date) =>
      TimeZoneUtils.detectUserTimezoneAndDisplayDate(
        new Date(dateString),
        'MMM dd, yyyy hh:mm a',
      ),
    forUiDateTimeUsingTimezone: (
      dateString: string | number | Date,
      timezone = 'Asia/Dhaka',
    ) =>
      formatInTimeZone(new Date(dateString), timezone, 'MMM dd, yyyy hh:mm a'),
    forTimezone: (
      dateString: string | number | Date,
      timezone = 'Asia/Dhaka',
    ) =>
      formatInTimeZone(new Date(dateString), timezone, 'MMM dd, yyyy hh:mm a'),
    forServerOnlyDate: (date: Date) => format(date, 'yyyy-MM-dd'),
    forBatchStartDate: (
      dateString: string | number | Date,
      locale: string | string[],
    ) => {
      const date = new Date(dateString);
      const dateLocale = locale === 'bn' ? bn : enUS;

      return format(date, locale === 'bn' ? 'do MMMM, EEEE' : 'do MMM, EE', {
        locale: dateLocale,
      });
    },
    forBatchClassRoutineAndTime: (
      classRoutines: BatchWithClassRoutine['classRoutines'],
      locale: string | string[],
    ) => {
      const dateLocale = locale === 'bn' ? bn : enUS;
      const classRoutineDays = classRoutines?.map(item => item.dayOfWeek);
      const Weekdays = getWeekDays(dateLocale);
      const classRoutineDaysName = classRoutineDays?.map(
        dayNumber => Weekdays[dayNumber],
      );

      const firstRoutineTime = classRoutines?.[0]?.classTime;

      const classTimeDate = firstRoutineTime
        ? (() => {
            const [hours, minutes] = firstRoutineTime.split(':').map(Number);
            const now = new Date();

            const utcDate = new Date(
              Date.UTC(
                now.getFullYear(),
                now.getMonth(),
                now.getDate(),
                hours,
                minutes,
                0,
              ),
            );

            const userTimezone = TimeZoneUtils.detectUserTimezone();

            return formatInTimeZone(utcDate, userTimezone, 'h:mm a');
          })()
        : '';

      const bengaliClassTime = getBengaliFormattedTime(classTimeDate);
      const formattedClassTime =
        locale === 'bn' ? bengaliClassTime : classTimeDate;

      return { classRoutineDaysName, formattedClassTime };
    },
    forClassDateAndTime: (dateString: string, locale: string) => {
      const dateLocale = locale === 'bn' ? bn : enUS;
      const userTimezone = TimeZoneUtils.detectUserTimezone();

      const dateTime = new Date(dateString);

      const zonedDate = toZonedTime(dateTime, userTimezone);

      const classDate = format(
        zonedDate,
        locale === 'bn' ? 'do MMMM, EEEE' : 'do MMM, EE',
        { locale: dateLocale },
      );

      const classTime = formatInTimeZone(dateTime, userTimezone, 'h:mm a');

      const bengaliClassTime = getBengaliFormattedTime(classTime);

      const formattedClassTime = locale === 'bn' ? bengaliClassTime : classTime;

      return { classDate, classTime: formattedClassTime };
    },
    formatMinutesToHours: (minutes: number, localeCode: string) => {
      const locale = localeCode === 'bn' ? bn : enUS;

      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;

      return formatDuration({ hours, minutes: mins }, { locale, zero: false });
    },
  },
  adjustHoursToTime(timeString: string, hoursToAdjust: number) {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();

    date.setHours(hours + hoursToAdjust, minutes, 0, 0);

    const resultHours = String(date.getHours()).padStart(2, '0');
    const resultMinutes = String(date.getMinutes()).padStart(2, '0');

    return `${resultHours}:${resultMinutes}`;
  },
};
